import React, { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { ScheduleConfig } from "../types/schedule";

/** 공백 뒤와 '(' 앞에 줄바꿈 기회(wbr) 삽입 */
function formatClassName(name: string): React.ReactNode {
  // 공백 뒤, 또는 '(' 앞에서 분할
  const tokens = name.split(/(?<=\s)|(?=[(])/);
  if (tokens.length <= 1) return name;
  return (
    <>
      {tokens.map((token, i) => (
        <React.Fragment key={i}>
          {i > 0 && <wbr />}
          {token}
        </React.Fragment>
      ))}
    </>
  );
}

export interface ClassData {
  id: number;
  className: string;
  teacher: string;
  classRoom: string | null;
  classCategory?: "regular" | "special" | "supplement";
  enrolledStudents?: string[];
  [key: string]: any;
}

export interface ClassBoxProps {
  cls: ClassData;
  dayIndex: number;
  startTime: string;
  endTime: string;
  order: number;
  hoveredKey: string | null;
  setHoveredKey: (key: string | null) => void;
  containerWidth: number;
  minClassHour: number;
  config: ScheduleConfig;
}

function toMinutes(t: string): number {
  const parts = t.split(":").map(Number);
  return (parts[0] || 0) * 60 + (parts[1] || 0);
}

function getNameHue(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }
  return hash % 360;
}

/** 공통 위치/크기/색상 계산 */
function useBoxLayout(props: ClassBoxProps) {
  const { cls, dayIndex, startTime, endTime, order, hoveredKey, containerWidth, minClassHour, config } = props;
  const { hourHeight, leftMarginPx, rightMarginPx, dayColumns } = config;

  const startMinutes = toMinutes(startTime);
  const endMinutes = toMinutes(endTime);
  const startOffsetMinutes = startMinutes - minClassHour * 60;
  const durationMinutes = endMinutes - startMinutes;
  const topPx = (startOffsetMinutes / 60) * hourHeight;
  const heightPx = (durationMinutes / 60) * hourHeight;

  const isEven = order > 0 ? order % 2 === 0 : false;
  const offsetFraction = isEven ? "1 / 3" : "0";
  const areaWidth = containerWidth - leftMarginPx - rightMarginPx;
  const dayColumnWidth = areaWidth / dayColumns;
  const left = `calc(${leftMarginPx}px + ${dayColumnWidth}px * ${dayIndex} + ${dayColumnWidth}px * ${offsetFraction})`;
  const width = `calc(${dayColumnWidth}px * 2 / 3)`;

  const isClassHovered = hoveredKey === String(cls.id);
  const hue = getNameHue(cls.className);
  const colors = {
    bg: isClassHovered ? `hsl(${hue}, 55%, 84%)` : `hsla(${hue}, 55%, 91%, 0.5)`,
    border: isClassHovered ? `hsl(${hue}, 65%, 55%)` : `hsl(${hue}, 40%, 72%)`,
    nameText: `hsl(${hue}, 85%, 25%)`,
    text: `hsl(${hue}, 50%, 35%)`,
    shadow: isClassHovered
      ? `0 4px 12px hsla(${hue}, 40%, 60%, 0.35)`
      : `0 1px 3px hsla(${hue}, 30%, 70%, 0.2)`,
  };

  const boxStyle: React.CSSProperties = {
    position: "absolute",
    left,
    top: `${topPx}px`,
    width,
    height: `${heightPx}px`,
    boxSizing: "border-box",
    backgroundColor: colors.bg,
    border: `1px solid ${colors.border}`,
    borderRadius: "8px",
    padding: "2px 5px",
    fontSize: 12,
    fontWeight: 600,
    overflow: "hidden",
    boxShadow: colors.shadow,
    display: "flex",
    flexDirection: "column",
    alignItems: isEven ? "flex-end" : "flex-start",
    textAlign: isEven ? "right" : "left",
    gap: 1,
    zIndex: isClassHovered ? 10 : order,
  };

  return { isEven, isClassHovered, colors, boxStyle };
}

// ── ClassBox: 시간표 페이지용 (수강생 목록 박스 안에 표시) ────

function ClassBox(props: ClassBoxProps) {
  const { cls, setHoveredKey } = props;
  const { isEven, colors, boxStyle } = useBoxLayout(props);
  const students = cls.enrolledStudents ?? [];

  return (
    <div
      style={boxStyle}
      onMouseEnter={() => setHoveredKey(String(cls.id))}
      onMouseLeave={() => setHoveredKey(null)}
    >
      <div style={{ fontSize: 11, fontWeight: 700, lineHeight: 1.2, color: colors.nameText, wordBreak: "keep-all" }}>
        {formatClassName(cls.className)}
      </div>
      {students.length > 0 && (
        <div
          style={{
            fontSize: 10,
            color: colors.text,
            lineHeight: 1.3,
            wordBreak: "keep-all",
            overflowWrap: "break-word",
            width: "100%",
            textAlign: isEven ? "right" : "left",
          }}
        >
          {students.join(", ")}
        </div>
      )}
    </div>
  );
}

// ── ClassBoxCompact: 시간표 일람용 (반 이름만, 마우스오버 팝오버) ──

function formatTime12(time: string): string {
  const [hStr, mStr] = time.split(":");
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  const suffix = h >= 12 ? "오후" : "오전";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${suffix} ${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

const DAYS_COMPACT = [
  { key: "monday", label: "월" },
  { key: "tuesday", label: "화" },
  { key: "wednesday", label: "수" },
  { key: "thursday", label: "목" },
  { key: "friday", label: "금" },
  { key: "saturday", label: "토" },
  { key: "sunday", label: "일" },
] as const;

function groupClsSchedules(cls: ClassData): [string, string[]][] {
  const map = new Map<string, string[]>();
  DAYS_COMPACT.forEach(({ key, label }) => {
    if (!cls[key]) return;
    const start = ((cls[`${key}StartTime`] as string) || "00:00").substring(0, 5);
    const end = ((cls[`${key}EndTime`] as string) || "00:00").substring(0, 5);
    const range = `${formatTime12(start)} ~ ${formatTime12(end)}`;
    if (!map.has(range)) map.set(range, []);
    map.get(range)!.push(label);
  });
  return Array.from(map.entries());
}

export function ClassBoxCompact(props: ClassBoxProps) {
  const { cls, startTime, endTime, setHoveredKey } = props;
  const { isClassHovered, colors, boxStyle } = useBoxLayout(props);
  const students = cls.enrolledStudents ?? [];
  const boxRef = useRef<HTMLDivElement>(null);
  const [popoverPos, setPopoverPos] = useState<{
    top?: number;
    bottom?: number;
    left: number;
  } | null>(null);

  const POPOVER_W = 248;
  const POPOVER_H_EST = 240; // 예상 최대 높이

  const handleMouseEnter = () => {
    setHoveredKey(String(cls.id));
    if (boxRef.current) {
      const rect = boxRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - 6;
      const spaceAbove = rect.top - 6;
      const left = Math.min(
        rect.left,
        window.innerWidth - POPOVER_W - 8
      );
      if (spaceBelow >= POPOVER_H_EST || spaceBelow >= spaceAbove) {
        setPopoverPos({ top: rect.bottom + 4, left });
      } else {
        // 아래 공간 부족 → 위쪽 표시
        setPopoverPos({ bottom: window.innerHeight - rect.top + 4, left });
      }
    }
  };

  const handleMouseLeave = () => {
    setHoveredKey(null);
    setPopoverPos(null);
  };

  return (
    <>
      <div
        ref={boxRef}
        style={boxStyle}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div style={{ fontSize: 11, fontWeight: 700, lineHeight: 1.2, color: colors.nameText, wordBreak: "keep-all" }}>
          {formatClassName(cls.className)}
        </div>
      </div>

      {isClassHovered && popoverPos &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: popoverPos.top,
              bottom: popoverPos.bottom,
              left: popoverPos.left,
              zIndex: 9999,
              backgroundColor: "#fff",
              border: "1px solid #e0e0e0",
              borderRadius: 8,
              boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
              padding: "10px 12px",
              minWidth: 140,
              maxWidth: 240,
              pointerEvents: "none",
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 13, color: "#111", marginBottom: 4 }}>
              {cls.className}
            </div>
            {cls.classRoom && (
              <div style={{ color: "#555", fontSize: 12, marginBottom: 4 }}>🚪 {cls.classRoom}</div>
            )}
            <div style={{ marginBottom: students.length ? 6 : 0 }}>
              {groupClsSchedules(cls).map(([timeRange, days], idx) => (
                <div key={idx} style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 3 }}>
                  <div style={{ display: "flex", gap: 2 }}>
                    {days.map((day) => (
                      <span key={day} style={{
                        fontSize: 10, fontWeight: 700, padding: "1px 4px", borderRadius: 3,
                        backgroundColor: day === "토" ? "#e3f2fd" : day === "일" ? "#ffebee" : "#f0f0f0",
                        color: day === "토" ? "#1565c0" : day === "일" ? "#c62828" : "#555",
                        border: `1px solid ${day === "토" ? "#90caf9" : day === "일" ? "#ef9a9a" : "#ddd"}`,
                      }}>{day}</span>
                    ))}
                  </div>
                  <span style={{ color: "#555", fontSize: 12 }}>{timeRange}</span>
                </div>
              ))}
            </div>
            {students.length > 0 && (
              <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: 5 }}>
                <div style={{ fontWeight: 600, color: "#333", fontSize: 11, marginBottom: 3 }}>수강생</div>
                <div style={{ color: "#444", fontSize: 11, lineHeight: 1.5 }}>
                  {students.join(", ")}
                </div>
              </div>
            )}
          </div>,
          document.body
        )}
    </>
  );
}

export default ClassBox;
